import types from '../mutation-types';
import BookingsAPI from '../../api/bookings';

/**
 * OCHAT: estado de Reservas.
 *
 * `availability` se guarda aparte de `records` a propósito: no son reservas, es el
 * resultado de una consulta de inventario que se descarta en cuanto cambian las
 * fechas. Mezclarlas obligaría a limpiar records en cada búsqueda.
 */
export const state = {
  records: [],
  availability: null,
  quote: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isCheckingAvailability: false,
    isQuoting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getBookings(_state) {
    return _state.records;
  },
  getBooking: _state => id =>
    _state.records.find(record => record.id === Number(id)),
  getAvailability(_state) {
    return _state.availability;
  },
  getQuote(_state) {
    return _state.quote;
  },
  /** Las reservas de un contacto, para el panel de la conversación. */
  getBookingsForContact: _state => contactId =>
    _state.records.filter(record => record.contact_id === Number(contactId)),
};

export const actions = {
  get: async ({ commit }, params = {}) => {
    commit(types.SET_BOOKING_UI_FLAG, { isFetching: true });
    try {
      const response = await BookingsAPI.get(params);
      commit(types.SET_BOOKINGS, response.data.payload);
    } finally {
      commit(types.SET_BOOKING_UI_FLAG, { isFetching: false });
    }
  },

  checkAvailability: async ({ commit }, params) => {
    commit(types.SET_BOOKING_UI_FLAG, { isCheckingAvailability: true });
    try {
      const response = await BookingsAPI.availability(params);
      commit(types.SET_BOOKING_AVAILABILITY, response.data);
      return response.data;
    } finally {
      commit(types.SET_BOOKING_UI_FLAG, { isCheckingAvailability: false });
    }
  },

  quote: async ({ commit }, payload) => {
    commit(types.SET_BOOKING_UI_FLAG, { isQuoting: true });
    try {
      const response = await BookingsAPI.quote(payload);
      commit(types.SET_BOOKING_QUOTE, response.data);
      return response.data;
    } finally {
      commit(types.SET_BOOKING_UI_FLAG, { isQuoting: false });
    }
  },

  create: async ({ commit }, payload) => {
    commit(types.SET_BOOKING_UI_FLAG, { isCreating: true });
    try {
      const response = await BookingsAPI.create(payload);
      commit(types.ADD_BOOKING, response.data);
      return response.data;
    } finally {
      commit(types.SET_BOOKING_UI_FLAG, { isCreating: false });
    }
  },

  reschedule: async ({ commit }, { id, ...payload }) => {
    commit(types.SET_BOOKING_UI_FLAG, { isUpdating: true });
    try {
      const response = await BookingsAPI.reschedule(id, payload);
      commit(types.EDIT_BOOKING, response.data);
      return response.data;
    } finally {
      commit(types.SET_BOOKING_UI_FLAG, { isUpdating: false });
    }
  },

  // Cancelar no borra: cambia el estado y devuelve el inventario. Por eso se hace
  // EDIT y no DELETE en el store — la reserva sigue existiendo y el equipo del hotel
  // necesita verla en el historial.
  cancel: async ({ commit }, id) => {
    commit(types.SET_BOOKING_UI_FLAG, { isDeleting: true });
    try {
      const response = await BookingsAPI.cancel(id);
      commit(types.EDIT_BOOKING, response.data);
      return response.data;
    } finally {
      commit(types.SET_BOOKING_UI_FLAG, { isDeleting: false });
    }
  },

  markNoShow: async ({ commit }, id) => {
    commit(types.SET_BOOKING_UI_FLAG, { isUpdating: true });
    try {
      const response = await BookingsAPI.markNoShow(id);
      commit(types.EDIT_BOOKING, response.data);
      return response.data;
    } finally {
      commit(types.SET_BOOKING_UI_FLAG, { isUpdating: false });
    }
  },
};

export const mutations = {
  [types.SET_BOOKING_UI_FLAG](_state, uiFlag) {
    _state.uiFlags = { ..._state.uiFlags, ...uiFlag };
  },
  [types.SET_BOOKINGS](_state, records) {
    _state.records = records || [];
  },
  [types.SET_BOOKING_AVAILABILITY](_state, availability) {
    _state.availability = availability;
  },
  [types.SET_BOOKING_QUOTE](_state, quote) {
    _state.quote = quote;
  },
  [types.ADD_BOOKING](_state, record) {
    _state.records = [record, ..._state.records];
  },
  [types.EDIT_BOOKING](_state, record) {
    const index = _state.records.findIndex(item => item.id === record.id);
    if (index === -1) {
      _state.records = [record, ..._state.records];
      return;
    }
    _state.records = [
      ..._state.records.slice(0, index),
      record,
      ..._state.records.slice(index + 1),
    ];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
